import { useState, useEffect } from 'react';
import { Card } from '../../components/common/Card';
import apiClient from '../../services/api';

interface Model {
  id: string;
  object: string;
  created: number;
  owned_by: string;
  provider?: string;
  health_status?: string;
}

interface ModelsListResponse {
  object: string;
  data: Model[];
}

export function ModelsPage() {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get<ModelsListResponse>('/models');
      setModels(response.data.data);
    } catch (error) {
      console.error('Failed to fetch models:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHealthStatusColor = (status?: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-100';
      case 'degraded':
        return 'text-yellow-600 bg-yellow-100';
      case 'unhealthy':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getHealthStatusText = (status?: string) => {
    switch (status) {
      case 'healthy':
        return '健康';
      case 'degraded':
        return '降级';
      case 'unhealthy':
        return '不可用';
      default:
        return '未知';
    }
  };

  const getProviderName = (provider?: string) => {
    switch (provider) {
      case 'openai':
        return 'OpenAI';
      case 'claude':
        return 'Anthropic';
      case 'wenxin':
        return '文心一言';
      case 'tongyi':
        return '通义千问';
      case 'custom':
        return '自定义';
      default:
        return provider || '未知';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">可用模型</h1>
        <p className="mt-2 text-gray-600">查看当前可用的AI模型列表</p>
      </div>

      <Card>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">加载中...</p>
          </div>
        ) : models.length === 0 ? (
          <p className="text-center text-gray-500 py-8">暂无可用模型</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">模型ID</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">提供商</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">健康状态</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">所有者</th>
                </tr>
              </thead>
              <tbody>
                {models.map((model) => (
                  <tr key={model.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-mono">{model.id}</td>
                    <td className="py-3 px-4 text-sm">{getProviderName(model.provider)}</td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getHealthStatusColor(
                          model.health_status
                        )}`}
                      >
                        {getHealthStatusText(model.health_status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">{model.owned_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
